import React from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import DealRoomListingPage from '../components/Succeedo/DealRoom/DealRoomListingPage';

const LISTING_QUERY = gql`
  query DealListing($id: ID!) {
    currentUser {
      id
      name
      succeedo_persona
    }
    deal_listing(id: $id) {
      id
      industry
      province
      years_in_operation
      revenue
      adjusted_ebitda
      employees
      asking_price
      deal_preferences
      timeline
      business_story
      key_strengths
      confidential
      deal_state
      masked_owner_name
      photo_urls
      is_owner
      expires_at
      days_until_expiry
      expiring_soon
      expired
      off_market
      closed_listing
      counterparty_name
      expected_close_date
      under_loi_at
      under_contract_at
      closed_at
    }
  }
`;

const RENEW = gql`
  mutation RenewDealListing($id: ID!) {
    renew_deal_listing(id: $id) {
      deal { id deal_state expires_at days_until_expiry expiring_soon }
      errors
    }
  }
`;

const MARK_UNDER_LOI = gql`
  mutation MarkUnderLoi($id: ID!, $counterparty_name: String, $expected_close_date: String) {
    mark_listing_under_loi(id: $id, counterparty_name: $counterparty_name, expected_close_date: $expected_close_date) {
      deal { id deal_state under_loi_at counterparty_name expected_close_date }
      errors
    }
  }
`;

const MARK_UNDER_CONTRACT = gql`
  mutation MarkUnderContract($id: ID!, $counterparty_name: String, $expected_close_date: String) {
    mark_listing_under_contract(id: $id, counterparty_name: $counterparty_name, expected_close_date: $expected_close_date) {
      deal { id deal_state under_contract_at counterparty_name expected_close_date }
      errors
    }
  }
`;

const MARK_CLOSED = gql`
  mutation MarkClosed($id: ID!, $outcome: String) {
    mark_listing_closed(id: $id, outcome: $outcome) {
      deal { id deal_state closed_at }
      errors
    }
  }
`;

const ROLLBACK = gql`
  mutation RollbackToPublished($id: ID!) {
    rollback_listing_to_published(id: $id) {
      deal { id deal_state under_loi_at under_contract_at }
      errors
    }
  }
`;

const PUBLISH = gql`
  mutation PublishDealListing($id: ID!) {
    publish_deal_listing(id: $id) {
      deal { id deal_state }
      errors
    }
  }
`;

const ARCHIVE = gql`
  mutation ArchiveDealListing($id: ID!) {
    archive_deal_listing(id: $id) {
      deal { id deal_state }
      errors
    }
  }
`;

const CLONE = gql`
  mutation CloneDealListing($source_id: ID!) {
    clone_deal_listing(source_id: $source_id) {
      deal { id }
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function ListingLoader({ listingId }) {
  const { data, loading, refetch } = useQuery(LISTING_QUERY, {
    variables: { id: listingId },
    fetchPolicy: 'cache-and-network',
  });

  const [publishMutation, { loading: publishing }] = useMutation(PUBLISH, {
    onCompleted: ({ publish_deal_listing }) => {
      if (publish_deal_listing?.errors?.length) {
        alert(publish_deal_listing.errors.join('\n'));
      } else {
        try { window.ahoy?.track('succeedo_deal_room_listing_published', { listing_id: listingId }); } catch (_e) {}
        refetch();
      }
    },
  });

  const [archiveMutation, { loading: archiving }] = useMutation(ARCHIVE, {
    onCompleted: ({ archive_deal_listing }) => {
      if (archive_deal_listing?.errors?.length) {
        alert(archive_deal_listing.errors.join('\n'));
      } else {
        refetch();
      }
    },
  });

  const [cloneMutation, { loading: cloning }] = useMutation(CLONE, {
    onCompleted: ({ clone_deal_listing }) => {
      if (clone_deal_listing?.errors?.length) {
        alert(clone_deal_listing.errors.join('\n'));
        return;
      }
      const newId = clone_deal_listing?.deal?.id;
      if (newId) window.location.href = `/succeedo/deal-room/${newId}/edit`;
    },
  });

  const [renewMutation, { loading: renewing }] = useMutation(RENEW, {
    onCompleted: ({ renew_deal_listing }) => {
      if (renew_deal_listing?.errors?.length) {
        alert(renew_deal_listing.errors.join('\n'));
      } else {
        try { window.ahoy?.track('succeedo_deal_room_listing_renewed', { listing_id: listingId }); } catch (_e) {}
        refetch();
      }
    },
  });

  // D15 lifecycle mutations — use a shared completion handler that
  // surfaces server errors to the user and refetches the listing on
  // success so the stepper UI re-renders against the new deal_state.
  const handleLifecycleResult = (resultKey, eventName) => ({ [resultKey]: payload }) => {
    if (payload?.errors?.length) {
      alert(payload.errors.join('\n'));
    } else {
      try { window.ahoy?.track(eventName, { listing_id: listingId }); } catch (_e) {}
      refetch();
    }
  };

  const [markUnderLoi, { loading: markingLoi }] = useMutation(MARK_UNDER_LOI, {
    onCompleted: handleLifecycleResult('mark_listing_under_loi', 'succeedo_deal_room_listing_under_loi'),
  });
  const [markUnderContract, { loading: markingContract }] = useMutation(MARK_UNDER_CONTRACT, {
    onCompleted: handleLifecycleResult('mark_listing_under_contract', 'succeedo_deal_room_listing_under_contract'),
  });
  const [markClosed, { loading: markingClosed }] = useMutation(MARK_CLOSED, {
    onCompleted: handleLifecycleResult('mark_listing_closed', 'succeedo_deal_room_listing_closed'),
  });
  const [rollback, { loading: rollingBack }] = useMutation(ROLLBACK, {
    onCompleted: handleLifecycleResult('rollback_listing_to_published', 'succeedo_deal_room_listing_rolled_back'),
  });

  const user = data?.currentUser;
  const listing = data?.deal_listing;
  // Server resolves is_owner from context[:current_user] so we never need
  // to expose owner_user_id (which would defeat confidentiality masking).
  const isOwner = listing?.is_owner === true;

  return (
    <DealRoomListingPage
      listing={listing}
      isOwner={isOwner}
      loading={loading || publishing || archiving || cloning || renewing || markingLoi || markingContract || markingClosed || rollingBack}
      persona={(user?.succeedo_persona || 'buyer').toUpperCase()}
      userName={user?.name || ''}
      userInitials={deriveInitials(user?.name)}
      onEdit={() => { window.location.href = `/succeedo/deal-room/${listingId}/edit`; }}
      onPublish={() => publishMutation({ variables: { id: listingId } })}
      onArchive={() => archiveMutation({ variables: { id: listingId } })}
      onRelist={() => {
        if (window.confirm('Re-list this business as a fresh draft? Your archived listing stays on file for your records.')) {
          cloneMutation({ variables: { source_id: listingId } });
        }
      }}
      onRequestIntro={() => alert('Request Intro flow ships with S3-A.')}
      onRenew={() => renewMutation({ variables: { id: listingId } })}
      onMarkUnderLoi={({ counterpartyName, expectedCloseDate }) => markUnderLoi({
        variables: { id: listingId, counterparty_name: counterpartyName, expected_close_date: expectedCloseDate },
      })}
      onMarkUnderContract={({ counterpartyName, expectedCloseDate }) => markUnderContract({
        variables: { id: listingId, counterparty_name: counterpartyName, expected_close_date: expectedCloseDate },
      })}
      onMarkClosed={({ outcome }) => markClosed({ variables: { id: listingId, outcome } })}
      onRollback={() => rollback({ variables: { id: listingId } })}
    />
  );
}

export default function SucceedoDealRoomListingContainer({ listingId }) {
  return (
    <ApolloProvider client={client}>
      <ListingLoader listingId={listingId} />
    </ApolloProvider>
  );
}
